import React from "react";
import { useIntl } from "react-intl";

import {
	ab0Messages,
	rhMessages
} from "lib/build/forms/ktx/basic-registration-data/clinical-data/bloodGroup";
import { Status } from "lib/build/forms/ktx/committeeOpinion";
import { statusByNephrologistMessages } from "lib/build/forms/ktx/patientCondition";

import { computeAge } from "src/common/hooks/useFormatAge";
import { useFormatMessageMap } from "src/common/hooks/useFormatMessageMap";
import { useFormatName } from "src/common/hooks/useFormatName";
import {
	getEllapsedTimeInMonths,
	getNumberOfDays
} from "src/common/utils/dates";
import { sexMessages } from "src/messages/generic";
import { queries } from "src/modules/kd/patient-list/queries";
import { Patient, WaitingListTime } from "src/modules/kd/patient-list/types";
import {
	longStatusMessages,
	shortStatusMessages
} from "src/modules/ktx/messages";
import { findEvent, RawPatient } from "src/modules/shared/patient-list";

export function useNormalizePatient(): (patient: RawPatient) => Patient {
	const formatMessage = useFormatMessageMap();
	const formatName = useFormatName();
	const { formatDate } = useIntl();

	return React.useCallback(
		(rawPatient: RawPatient): Patient => {
			const {
				id,
				modifTime,
				socialSecurityNumber,
				title = "",
				firstName,
				lastName,
				sex,
				dateOfBirth,
				extra
			} = rawPatient;

			const statusEvent = findEvent<string>(extra, queries.status);
			const addressEvent = findEvent<{ city: string }>(
				extra,
				queries.address
			);
			const weightEvent = findEvent<number>(extra, queries.weight);
			const ab0Event = findEvent<string>(extra, queries.ab0);
			const rhEvent = findEvent<string>(extra, queries.rh);

			const waitingListEvent = findEvent<Date>(
				extra,
				queries.waitingListStartDate
			);

			const statusByNephrologistEvent = findEvent<string>(
				extra,
				queries.statusByNephrologist
			);

			const { value: statusValue = Status.NEW } = statusEvent || {};
			const status = {
				value: statusValue,
				shortLabel: formatMessage(shortStatusMessages, statusValue),
				longLabel: formatMessage(longStatusMessages, statusValue)
			};

			const name = formatName({
				title,
				firstName,
				lastName
			});

			let bloodGroup = "";
			if (ab0Event !== null && rhEvent !== null) {
				const ab0Label = formatMessage(ab0Messages, ab0Event.value);
				const rhLabel = formatMessage(rhMessages, rhEvent.value);
				bloodGroup = `${ab0Label} RH ${rhLabel}`;
			}

			let waitingListTime: WaitingListTime | undefined;
			if (waitingListEvent !== null) {
				const now = new Date();
				const waitingListMonths = getEllapsedTimeInMonths(
					new Date(waitingListEvent.value),
					now
				);
				const waitingListDays = getNumberOfDays(
					waitingListEvent.value,
					now
				);

				waitingListTime = {
					days: waitingListDays,
					months: waitingListMonths
				};
			}

			let statusByNephrologist = "";
			if (statusByNephrologistEvent) {
				statusByNephrologist = formatMessage(
					statusByNephrologistMessages,
					statusByNephrologistEvent.value
				);
			}

			const formatDateOpts = {
				year: "numeric",
				month: "numeric",
				day: "numeric",
				hour: "numeric",
				minute: "numeric",
				second: "numeric"
			} as const;

			return {
				id,
				modifTime: formatDate(modifTime, formatDateOpts),
				status,
				name,
				socialSecurityNumber,

				age: computeAge(dateOfBirth),
				sex: formatMessage(sexMessages, sex),
				city: addressEvent === null ? "" : addressEvent.value.city,
				weight: weightEvent,
				bloodGroup,

				waitingListTime,
				statusByNephrologist
			};
		},
		[formatMessage, formatName, formatDate]
	);
}
